import { datadogRum } from "@datadog/browser-rum";
import { SetStateAction, useAtom, useAtomValue } from "jotai";
import { useCallback, useState } from "react";
import { useMutation } from "react-query";

import { UnUploadedImage } from "components/PhotoUpload";

import { createSignedUrl } from "api/createSignedUrl";
import { uploadPhotoToSignedUrl } from "api/uploadPhotoToSignedUrl";
import { authoritiesPhotosAtom } from "atoms/authoritiesPhotos";
import { caiPhotosAtom } from "atoms/caiPhotos";
import {
  authoritiesUploadedPhotosAtom,
  caiUploadedPhotosAtom,
  injuredUploadedPhotosAtom,
} from "atoms/digitalJourneyPhotos";
import { injuredPeoplePhotosAtom } from "atoms/injuredPeoplePhotos";
import { allPhotosToUploadAtom, photoUploadCompleteAtom, uploadedPhotosAtom } from "atoms/photosUpload";
import { sessionIdAtom } from "atoms/sessionId";
import { getImageFromBase64 } from "utils/imageProcessing/base64Converter";
import { shouldRetry } from "utils/networkRequestHelper";

import { clientIdAtom } from "../../../atoms/clientId";
import {
  CreateSignedUrlResponse,
  MappedUploadedPhotos,
  SignedUrlVariables,
  UploadVariables,
  UploadedPhoto,
} from "../types";
import { signatureImageAtom } from "atoms/signature";

export const useUploadPhotos = () => {
  const sessionId = useAtomValue(sessionIdAtom);
  const clientId = useAtomValue(clientIdAtom);
  const allPhotos = useAtomValue(allPhotosToUploadAtom);
  const caiPhotos = useAtomValue(caiPhotosAtom);
  const injuredPeoplePhotos = useAtomValue(injuredPeoplePhotosAtom);
  const authoritiesPhotos = useAtomValue(authoritiesPhotosAtom);
  const [photoUploadComplete, setPhotoUploadComplete] = useAtom(photoUploadCompleteAtom);
  const [uploadedPhotos, setUploadedPhotos] = useAtom(uploadedPhotosAtom);
  const [caiUploadedPhotos, setCaiUploadedPhotos] = useAtom(caiUploadedPhotosAtom);
  const [injuredUploadedPhotos, setInjuredUploadedPhotos] = useAtom(injuredUploadedPhotosAtom);
  const [authoritiesUploadedPhotos, setAuthoritiesUploadedPhotos] = useAtom(authoritiesUploadedPhotosAtom);
  const [hasError, setHasError] = useState(false);
  const signatureImage = useAtomValue(signatureImageAtom);

  const { mutateAsync: generateSignedUrl } = useMutation<CreateSignedUrlResponse | undefined, any, SignedUrlVariables>(
    async ({ photoId, sessionId }) => {
      if (
        uploadedPhotos[photoId] ||
        caiUploadedPhotos[photoId] ||
        injuredUploadedPhotos[photoId] ||
        authoritiesUploadedPhotos[photoId]
      )
        return;

      return createSignedUrl(sessionId, clientId);
    },
    {
      retry: shouldRetry,
      onError: (error) => {
        datadogRum.addError(error, {
          message: "Failed to get signed url",
        });
      },
    }
  );

  const { mutateAsync: upload } = useMutation<any, any, UploadVariables>(
    async ({ url, photo, id }) => {
      if (uploadedPhotos[id] || caiUploadedPhotos[id] || injuredUploadedPhotos[id] || authoritiesUploadedPhotos[id])
        return;

      return uploadPhotoToSignedUrl(url, photo);
    },
    {
      retry: shouldRetry,
      onError: (error) => {
        datadogRum.addError(error, {
          message: "Failed to upload image",
        });
      },
    }
  );

  const uploadAllImages = useCallback(async () => {
    setHasError(false);

    const successfulUploadResults: boolean[] = await Promise.all([
      ...allPhotos.map((image) => uploadImage(image, setUploadedPhotos)),
      ...caiPhotos.map((image) => uploadImage(image, setCaiUploadedPhotos)),
      ...injuredPeoplePhotos.map((image) => uploadImage(image, setInjuredUploadedPhotos)),
      ...authoritiesPhotos.map((image) => uploadImage(image, setAuthoritiesUploadedPhotos)),
      ...(signatureImage ? [uploadImage(signatureImage, setUploadedPhotos)] : []),
    ]);

    if (successfulUploadResults.every((val) => val)) {
      setPhotoUploadComplete(true);
    } else {
      setHasError(true);
    }
  // All non-stable values consumed inside uploadAllImages must be listed here to prevent stale
  // closures on retries. This includes mutable arrays and Jotai atom setters (not guaranteed
  // referentially stable), but excludes useState setters like setHasError which are stable by
  // design. See EST-15394 for a real production case caused by this.
  }, [
    allPhotos,
    caiPhotos,
    injuredPeoplePhotos,
    authoritiesPhotos,
    signatureImage,
    generateSignedUrl,
    sessionId,
    setPhotoUploadComplete,
    setUploadedPhotos,
    setCaiUploadedPhotos,
    setInjuredUploadedPhotos,
    setAuthoritiesUploadedPhotos,
    upload,
  ]);

  const uploadImage = async (
    image: UnUploadedImage,
    setter: (update: SetStateAction<MappedUploadedPhotos>) => Promise<void>
  ) => {
    const { id, base64, alt, type, clientImageId } = image;
    try {
      const signedUrl = await generateSignedUrl({ sessionId, photoId: id });

      if (!signedUrl) return true;

      const {
        data: { url, path, format, bucket, key },
      } = signedUrl;

      const photo = await getImageFromBase64(base64, alt);

      await upload({ url, photo, id });

      const uploadedPhoto: UploadedPhoto = {
        type,
        path,
        bucket,
        format,
        key,
        id,
        clientImageId,
      };

      setter((uploaded) => ({ ...uploaded, [id]: uploadedPhoto }));

      return true;
    } catch (error) {
      datadogRum.addError(error, { message: `failed to upload photo ${id}, ${type}, ${clientImageId}` });
      return false;
    }
  };

  return { uploadAllImages, photoUploadError: hasError, photoUploadComplete };
};
